import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { getPhotos, updatePhotoStatus, updatePhotoDiscountCode } from "@/services/photoStorage";
import { Photo } from "@/types/photos";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import UploadLinkSection from "@/components/dashboard/UploadLinkSection";
import { generateOneTimeDiscountCode, sendDiscountCodeEmail, isShopifyConfigured } from "@/services/shopifyService";
import { downloadPhoto } from "@/utils/photoDownloader";
import PendingPhotosTab from "@/components/dashboard/PendingPhotosTab";
import ApprovedPhotosTab from "@/components/dashboard/ApprovedPhotosTab";
import RejectedPhotosTab from "@/components/dashboard/RejectedPhotosTab";
import SettingsTab from "@/components/dashboard/SettingsTab";
import SearchBar from "@/components/dashboard/SearchBar";
import { generateMockPhotos, countPhotosByStatus, filterPhotosBySearchAndStatus } from "@/utils/photoUtils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getBrandPhotos } from "@/services/photoStorage";
import EmailSettingsTab from "@/components/dashboard/EmailSettingsTab";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronDown } from "lucide-react";
import SetupController from "@/components/dashboard/SetupController";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Dashboard = () => {
  const { toast } = useToast();
  const { brand } = useAuth();
  const [activeTab, setActiveTab] = useState<"pending" | "approved" | "rejected">("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [shopifyConfigured, setShopifyConfigured] = useState(isShopifyConfigured());
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const loadPhotos = async () => {
      try {
        setLoading(true);
        if (!brand?.id) return;

        const brandPhotos = await getBrandPhotos(brand.id);
        setPhotos(brandPhotos);
      } catch (error) {
        console.error("Error loading photos:", error);
        toast({
          title: "Error",
          description: "Failed to load photos",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadPhotos();
    setShopifyConfigured(isShopifyConfigured());
  }, [brand?.id, toast]);

  console.log("brand", brand);

  const handleApprove = async (id: string, customer_email: string) => {
    try {
      const discountCode = await generateOneTimeDiscountCode(
        customer_email,
        brand?.shopify_access_token,
        brand?.shopify_store_url
      );
      console.log("\x1b[34mDiscount code:", discountCode, "\x1b[0m");
      await updatePhotoStatus(id, "approved");
      setPhotos(photos.map((photo) => (photo.id === id ? { ...photo, status: "approved" } : photo)));
      toast({
        title: "Success",
        description: "Photo approved successfully",
      });
    } catch (error) {
      console.error("Error approving photo:", error);
      toast({
        title: "Error",
        description: "Failed to approve photo",
        variant: "destructive",
      });
    }
  };

  const handleReject = async (id: string) => {
    try {
      await updatePhotoStatus(id, "rejected");
      setPhotos(photos.map((photo) => (photo.id === id ? { ...photo, status: "rejected" } : photo)));
      toast({
        title: "Success",
        description: "Photo rejected successfully",
      });
    } catch (error) {
      console.error("Error rejecting photo:", error);
      toast({
        title: "Error",
        description: "Failed to reject photo",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (photo: Photo) => {
    try {
      const response = await fetch(photo.imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `photo-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading photo:", error);
      toast({
        title: "Error",
        description: "Failed to download photo",
        variant: "destructive",
      });
    }
  };

  const filteredPhotos = photos.filter((photo) => {
    const matchesSearch =
      photo.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      photo.productName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = photo.status === activeTab;
    return matchesSearch && matchesTab;
  });

  const tabOptions = [
    { value: "pending", label: `Pending${countPhotosByStatus(photos).pending > 0 ? ` (${countPhotosByStatus(photos).pending})` : ''}` },
    { value: "approved", label: `Approved${countPhotosByStatus(photos).approved > 0 ? ` (${countPhotosByStatus(photos).approved})` : ''}` },
    { value: "rejected", label: `Rejected${countPhotosByStatus(photos).rejected > 0 ? ` (${countPhotosByStatus(photos).rejected})` : ''}` },
    { value: "settings", label: "Settings" },
    { value: "email-settings", label: "Email Settings" }
  ];

  if (!brand) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg text-gray-600">Please log in to view your dashboard</p>
      </div>
    );
  }

  const dashboardContent = (
    <>
      <Navbar />

      <main className="min-h-screen py-8 md:py-16">
        <div className="container px-4 md:px-6">
          <div className="mb-6 md:mb-10">
            <h1 className="text-2xl md:text-3xl font-bold">Brand Dashboard</h1>
            <p className="text-muted-foreground mt-2 text-sm md:text-base">{brand && `Manage photo submissions for ${brand.name}`}</p>
          </div>

          <UploadLinkSection />

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 md:space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              {/* Mobile Dropdown */}
              <div className="md:hidden w-full">
                <Select value={activeTab} onValueChange={setActiveTab}>
                  <SelectTrigger className="w-full">
                    <SelectValue>
                      {tabOptions.find(tab => tab.value === activeTab)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {tabOptions.map(tab => (
                      <SelectItem key={tab.value} value={tab.value}>
                        {tab.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Desktop Tabs */}
              <TabsList className="hidden md:grid grid-cols-5 w-full">
                {tabOptions.map(tab => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="text-sm px-4 py-2"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {(activeTab === "pending" || activeTab === "approved" || activeTab === "rejected") &&
                filteredPhotos.length > 0 && (
                  <div className="w-full md:w-auto">
                    <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                  </div>
                )}
            </div>

            <TabsContent value="pending" className="mt-0">
              <PendingPhotosTab
                photos={filteredPhotos}
                processingId={processingId}
                onApprove={handleApprove}
                onReject={handleReject}
                shopifyConfigured={shopifyConfigured}
              />
            </TabsContent>

            <TabsContent value="approved" className="mt-0">
              <ApprovedPhotosTab photos={filteredPhotos} onDownload={handleDownload} />
            </TabsContent>

            <TabsContent value="rejected" className="mt-0">
              <RejectedPhotosTab photos={filteredPhotos} />
            </TabsContent>

            <TabsContent value="settings" className="mt-0">
              <SettingsTab shopifyConfigured={shopifyConfigured} />
            </TabsContent>

            <TabsContent value="email-settings" className="mt-0">
              <EmailSettingsTab />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </>
  );

  // Wrap with SetupController to show setup wizard for new users
  return (
    <SetupController>
      {dashboardContent}
    </SetupController>
  );
};

export default Dashboard;
